import { motion } from "framer-motion";
import { Award, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/ScrollReveal";

/**
 * Decorative SVG badge "Expert PA" used inline.
 */
function ExpertBadge({ size = 144 }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 144 144"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Badge Expert en Prévention Active"
            className="flex-shrink-0 drop-shadow-[0_15px_30px_rgba(232,135,60,0.3)]"
        >
            <defs>
                <linearGradient id="ringG" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#E8873C" />
                    <stop offset="100%" stopColor="#D9772C" />
                </linearGradient>
            </defs>
            {/* outer ring */}
            <circle cx="72" cy="72" r="66" stroke="url(#ringG)" strokeWidth="2" />
            {/* inner ring */}
            <circle cx="72" cy="72" r="58" stroke="#E8873C" strokeWidth="1" strokeDasharray="2 4" opacity="0.55" />
            {/* center disc */}
            <circle cx="72" cy="72" r="44" fill="#E8873C" />
            {/* "EPA" text */}
            <text
                x="72"
                y="83"
                textAnchor="middle"
                fontFamily="Fraunces, serif"
                fontSize="30"
                fontStyle="italic"
                fontWeight="500"
                fill="#FFFFFF"
                letterSpacing="0.5"
            >
                EPA
            </text>
            {/* small star ornament */}
            <path d="M72 16 L74 22 L80 22 L75 26 L77 32 L72 28 L67 32 L69 26 L64 22 L70 22 Z" fill="#E8873C" />
        </svg>
    );
}

export default function ExpertSection() {
    return (
        <section
            className="relative overflow-hidden bg-brand-sand py-24 md:py-32"
            data-testid="section-expert"
        >
            <div className="mx-auto max-w-6xl px-6">
                <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-[auto_1fr] md:gap-20">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.85, rotate: -8 }}
                        whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                        viewport={{ once: true, margin: "-15%" }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        className="mx-auto md:mx-0"
                    >
                        <ExpertBadge size={180} />
                    </motion.div>
                    <div>
                        <Reveal>
                            <span className="text-[11px] font-semibold uppercase tracking-[0.32em] text-brand-orange">
                                Statut reconnu
                            </span>
                            <h2 className="mt-5 font-display font-normal leading-[1.02] tracking-tightest text-brand-ink text-[40px] sm:text-5xl lg:text-[64px]">
                                Devenez <em className="italic text-brand-orange">Expert</em>
                                <br className="hidden lg:block" />{" "}
                                en Prévention Active.
                            </h2>
                            <p className="mt-7 max-w-2xl text-base leading-relaxed text-brand-ink/75 md:text-lg">
                                Un statut reconnu, délivré par Cursus Physio à l'issue d'un cursus complet (3 formations, 21h).
                                Il matérialise votre maîtrise d'une approche thérapeutique différenciante — celle qui anticipe,
                                raisonne, dure, motive.
                            </p>

                            <div className="mt-8 flex flex-col gap-5 sm:flex-row sm:items-center">
                                <Link
                                    to="/expertise-pa"
                                    data-testid="expert-section-cta"
                                    className="group inline-flex items-center gap-2 rounded-full bg-brand-ink px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-brand-coal"
                                >
                                    Voir les cursus
                                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                </Link>
                                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-brand-smoke">
                                    <ShieldCheck className="h-4 w-4 text-brand-orange" />
                                    Valable à vie · Communauté privée
                                </span>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
}

export { ExpertBadge };
